import React, { useEffect, useState, useMemo } from 'react';
import { ProSidebar, Menu, MenuItem } from 'react-pro-sidebar';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import clientsIcon from '../icons/users.svg';
import userIcon from '../icons/user.svg';
import purchasesIcon from '../icons/corner-down-left.svg';
import catalogIcon from '../icons/book-open.svg';
import dashboardIcon from '../icons/grid.svg';
import ordersIcon from '../icons/package.svg';
import repoIcon from '../icons/layers.svg';
import chevronUp from '../icons/chevron-upG.svg';
import chevronDown from '../icons/chevron-downG.svg';
import logoGF from '../images/logo2.png';
import paymentIcon from '../icons/payment.svg';
import performanceIcon from '../icons/performance.svg';
import calendarIcon from '../icons/calendarIcon.svg';
import operationsIcon from '../icons/operations.svg';

const Sidebar = () => {
  const location = useLocation();
  const [expandedMenu, setExpandedMenu] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const { t } = useTranslation();

  const handleImageError = (e) => {
    e.target.src = logoGF;
    e.target.style.width = "150px";
    e.target.style.height = "50px";
  };

  const roleBasedModules = {
    ROLE_ADMIN: {
      menus: ['calendar', 'clients', 'projects'],
      submenus: {
        catalog: ['brands', 'families', 'products', 'services', 'materiels'],
        purchases: ['history', 'providers'],
        sales: ['orderList', 'deliveryNotesList', 'creditNotesList'],
        //operations: ['ficheReceptionList', 'quoteList', 'orderList', 'deliveryNotesList', 'invoiceList', 'creditNotesList', 'afournisseurList', 'afinancierList'],
        repo: ['stock', 'transfers', 'transferRequests', 'providers', 'repos'],
        administration: ['users', 'banks', 'paymentTypes', 'vehicleBrands', 'insurers', 'vehicleModels', 'documentCategories'],
      },
    },
    ROLE_SERVICE_CONSULTANT: {
      menus: ['dashboard', 'calendar', 'clients', 'projects'],
      submenus: {
        operations: ['ficheReceptionList', 'orderList', 'invoiceList'],
        catalog: ['brands', 'families', 'products', 'services', 'materiels'],
        repo: ['stock', 'repos'],
        administration: ['users', 'banks', 'paymentTypes', 'vehicleBrands', 'vehicleModels', 'insurers', 'documentCategories'],
      },
      menuOrder: ['dashboard', 'calendar', 'clients', 'operations', 'projects', 'catalog', 'repo', 'administration'],
    },
    ROLE_WORKSHOP_MANAGER: {
      menus: ['dashboard', 'clients', 'projects'],
      submenus: {
        catalog: ['brands', 'families', 'products', 'services', 'materiels'],
        operations: ['ficheReceptionList', 'orderList', 'invoiceList'],
        repo: ['stock', 'transfers', 'repos'],
        administration: ['users', 'vehicleBrands', 'vehicleModels'],
      },
      menuOrder: ['dashboard', 'clients', 'operations', 'projects', 'catalog', 'repo', 'administration'],
    },
    ROLE_STOREKEEPER: {
      menus: ['dashboard'],
      submenus: {
        catalog: ['brands', 'families', 'products', 'services', 'materiels'],
        sales: ['orderList', 'deliveryNotesList', 'creditNotesList'],
        purchases: ['history', 'providers'],
        repo: ['stock', 'transfers', 'repos'],
      },
      menuOrder: ['dashboard', 'catalog', 'sales', 'purchases', 'repo'],
    },
    ROLE_BILLING_AGENT: {
      menus: ['dashboard', 'clients', 'projects'],
      submenus: {
        catalog: ['brands', 'families', 'products', 'services', 'materiels'],
        operations: ['ficheReceptionList', 'invoiceList', 'expenses'],
        administration: ['banks', 'paymentTypes', 'insurers', 'documentCategories'],
      },
      menuOrder: ['dashboard', 'clients', 'operations', 'projects', 'catalog', 'administration'],

    },
    ROLE_TECHNICIAN: {
      menus: ['kanban'],

    },
  };

  const submenus = useMemo(() => [
    {
      title: t(`titles.catalog`),
      key: 'catalog',
      icon: catalogIcon,
      items: [
        { title: t(`titles.brands`), path: '/catalog/brands', key: 'brands' },
        { title: t(`titles.families`), path: '/catalog/families', key: 'families' },
        { title: t(`titles.products`), path: '/catalog/products', key: 'products' },
        { title: t(`titles.services`), path: '/catalog/services', key: 'services' },
        { title: t(`titles.materiels`), path: '/catalog/materiels', key: 'materiels' },
      ],
    },
    {
      title: t(`titles.sb_purchases`),
      key: 'purchases',
      icon: purchasesIcon,
      items: [
        { title: t(`titles.purchases`), path: '/purchases', key: 'history' }, 
        { title: t(`titles.sb_providers`), path: '/providers', key: 'providers' }
      ],
    },
    {
      title: t(`titles.operations`),
      key: 'operations',
      icon: operationsIcon,
      items: [
        { title: t(`titles.fiche_reception`), path: '/fiche-reception', key: 'ficheReceptionList' },
        { title: t(`titles.orders`), path: '/orders', key: 'orderList' },
        { title: t(`titles.invoices`), path: '/invoices', key: 'invoiceList' },
        { title: t(`titles.expenses`), path: '/expenses', key: 'expenses' }
      ],
    },
    {
      title: t(`titles.operations`),
      key: 'sales',
      icon: ordersIcon,
      items: [
        { title: t(`titles.orders`), path: '/orders', key: 'orderList' },
        { title: t(`titles.bl`), path: '/delivery-notes', key: 'deliveryNotesList' },
        { title: t(`titles.br`), path: '/credit-notes', key: 'creditNotesList' },
      ],
    },
    {
      title: t(`titles.repo`),
      key: 'repo',
      icon: repoIcon,
      items: [
        { title: t(`titles.repo`), path: '/products-by-repo', key: 'stock' },
        { title: t(`titles.transfers`), path: '/transfers', key: 'transfers' },
        { title: t(`titles.sb_repositories`), path: '/repos', key: 'repos' },
      ],
    },
    {
      title: t(`titles.admin`),
      key: 'administration',
      icon: userIcon,
      items: [
        { title: t(`titles.sb_users`), path: '/users', key: 'users' },
        { title: t(`titles.banks`), path: '/banks', key: 'banks' },
        { title: t(`titles.paymentTypes`), path: '/payment-types', key: 'paymentTypes' },
        { title: t(`titles.vehicleBrands`), path: '/vehicle-brands', key: 'vehicleBrands' },
        { title: t(`titles.vehicleModels`), path: '/vehicle-models', key: 'vehicleModels' },
        { title: t(`titles.insurers`), path: '/insurers', key: 'insurers' },
        { title: t(`titles.documentCategories`), path: '/document-categories', key: 'documentCategories' },
      ],
    },
  ], [t]);

  const MENU_ITEMS = [
    {
      key: "dashboard",
      titleKey: "titles.dashboard",
      to: "/",
      icon: dashboardIcon,
      alt: "Dashboard",
    },
    {
      key: "calendar",
      titleKey: "titles.calendar",
      to: "/calendrier",
      icon: calendarIcon,
      alt: "Calendar",
    },
    {
      key: "clients",
      titleKey: "titles.clients",
      to: "/clients",
      icon: clientsIcon,
      alt: "Clients",
    },
    {
      key: "projects",
      titleKey: "titles.projects",
      to: "/projects",
      icon: repoIcon,
      alt: "Projects",
    },
    {
      key: "kanban",
      titleKey: "titles.kanban",
      to: "/kanban",
      icon: catalogIcon,
      alt: "Kanban",
    },
  ];

  useEffect(() => {
    const roleStore = localStorage.getItem('user');
    const role = roleStore ? JSON.parse(roleStore).role : null;
    setUserRole(role);
  }, []);

  useEffect(() => {
    const currentPath = location.pathname;

    const matchingMenu = submenus.find((submenu) =>
      submenu.items.some((item) => currentPath.startsWith(item.path))
    );

    if (matchingMenu) {
      setExpandedMenu(matchingMenu.key);
    }
  }, [location.pathname, submenus]);

  const handleToggle = (menuName) => {
    setExpandedMenu(expandedMenu === menuName ? null : menuName);
  };

  const isActiveSubMenu = (path) => location.pathname.startsWith(path);

const primaryColor = '#3872B0';

  const filteredMenuItems = MENU_ITEMS.filter((menuItem) =>
    roleBasedModules[userRole]?.menus?.includes(menuItem.key)
  );

  const filteredSubmenus = submenus
    .map((submenu) => ({
      ...submenu,
      items: submenu.items.filter(
        (item) => roleBasedModules[userRole]?.submenus?.[submenu.key]?.includes(item.key)
      ),
    }))
    .filter((submenu) => submenu.items.length > 0);

  const logo = localStorage.getItem('logo') ? localStorage.getItem('logo') : logoGF;

  return (
    <ProSidebar
      className="h-screen overflow-y-auto bg-[#F8F9FC] shadow-lg"
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Logo Section */}
      <div className="flex items-center justify-center h-[140px]">
        <img src={logo} alt="Company Logo" className="h-10" onError={handleImageError} />
      </div>

      <Menu iconShape="square">
        {/* Render items in custom order if menuOrder exists */}
        {roleBasedModules[userRole]?.menuOrder ? (
          roleBasedModules[userRole].menuOrder.map((itemKey) => {
            // Check if it's a menu item
            const menuItem = filteredMenuItems.find(item => item.key === itemKey);
            if (menuItem) {
              const { key, titleKey, to, icon, alt } = menuItem;
              const active = to === "/" 
                ? location.pathname === to 
                : location.pathname.startsWith(to);
              return (
                <MenuItem
                  key={key}
                  active={active}
                  onClick={() => setExpandedMenu(null)}
                  className={`flex items-center px-4 py-3 ${
                    active ? "bg-primary/15 text-primary" : "text-[#231E29]"
                  }`}
                  style={{
                    borderRadius: "8px",
                    margin: "0 10px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Link to={to} className="w-full h-full flex items-center">
                    <span className="flex items-center gap-3">
                      <div
                        style={{
                          width: '20px',
                          height: '20px',
                          backgroundColor: active ? primaryColor : '#000',
                          WebkitMaskImage: `url(${icon})`,
                          WebkitMaskSize: 'contain',
                          WebkitMaskRepeat: 'no-repeat',
                          WebkitMaskPosition: 'center',
                          maskImage: `url(${icon})`,
                          maskSize: 'contain',
                          maskRepeat: 'no-repeat',
                          maskPosition: 'center',
                        }}
                      />
                      <span className={`${active ? 'font-semibold' : 'font-medium'}`}>{t(titleKey)}</span>
                    </span>
                  </Link>
                </MenuItem>
              );
            }
            
            // Check if it's a submenu
            const submenu = filteredSubmenus.find(sm => sm.key === itemKey);
            if (submenu) {
              const hasActiveChild = submenu.items.some((item) => isActiveSubMenu(item.path));
              const isSubmenuActive = expandedMenu === submenu.key || hasActiveChild;
              
              return (
                <div key={submenu.key} className="flex flex-col w-full">
                  {/* Parent Menu */}
                  <MenuItem
                    onClick={() => handleToggle(submenu.key)}
                    className={`px-4 py-3 ${isSubmenuActive
                      ? 'bg-primary/15 text-primary'
                      : 'text-[#16283E]'
                      }`}
                    style={{
                      borderRadius: '8px',
                      margin: '0 10px',
                    }}
                  >
                    <div className="flex items-center justify-between w-full">
                      {/* Title and Icon */}
                      <div className="flex items-center gap-3">
                        <div
                          style={{
                            width: '20px',
                            height: '20px',
                            backgroundColor: isSubmenuActive ? primaryColor : '#000',
                            WebkitMaskImage: `url(${submenu.icon})`,
                            WebkitMaskSize: 'contain',
                            WebkitMaskRepeat: 'no-repeat',
                            WebkitMaskPosition: 'center',
                            maskImage: `url(${submenu.icon})`,
                            maskSize: 'contain',
                            maskRepeat: 'no-repeat',
                            maskPosition: 'center',
                          }}
                        />
                        <span className={`${isSubmenuActive ? 'font-semibold' : 'font-medium'}`}>{submenu.title}</span>
                      </div>
                      {/* Chevron */}
                      <div className="ml-auto">
                        <img
                          src={expandedMenu === submenu.key ? chevronUp : chevronDown}
                          alt="Chevron"
                          className="w-5 h-5"
                        />
                      </div>
                    </div>
                  </MenuItem>

                  {/* Submenu Items */}
                  {expandedMenu === submenu.key && (
                    <div className="pl-8 pt-2">
                      {submenu.items.map((item) => (
                        <MenuItem
                          key={item.path}
                          active={isActiveSubMenu(item.path)}
                          className={`relative px-4 py-2 ${isActiveSubMenu(item.path)
                            ? 'text-primary bg-transparent before:absolute before:left-0 before:top-0 before:bottom-0 before:w-[4px] before:bg-primary'
                            : 'text-[#16283E] before:absolute before:left-0 before:top-0 before:bottom-0 before:w-[1px] before:bg-gray-300'
                            }`}
                        >
                          <Link to={item.path} className={`${isActiveSubMenu(item.path) ? 'font-semibold' : 'font-medium'} w-full h-full flex items-center hover:text-primary transition-colors`}
                          >{item.title}</Link>
                        </MenuItem>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            return null;
          })
        ) : (
          <>
            {/* Default rendering: Menu Items first */}
            {filteredMenuItems.map(({ key, titleKey, to, icon, alt }) => {
              // For dashboard ("/"), use exact match; for others, use startsWith
              const active = to === "/" 
                ? location.pathname === to 
                : location.pathname.startsWith(to);
              return (
                <MenuItem
                  key={key}
                  active={active}
                  onClick={() => setExpandedMenu(null)}
                  className={`flex items-center px-4 py-3 ${
                    active ? "bg-primary/15 text-primary" : "text-[#231E29]"
                  }`}
                  style={{
                    borderRadius: "8px",
                    margin: "0 10px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Link to={to} className="w-full h-full flex items-center">
                    <span className="flex items-center gap-3">
                      <div
                        style={{
                          width: '20px',
                          height: '20px',
                          backgroundColor: active ? primaryColor : '#000',
                          WebkitMaskImage: `url(${icon})`,
                          WebkitMaskSize: 'contain',
                          WebkitMaskRepeat: 'no-repeat',
                          WebkitMaskPosition: 'center',
                          maskImage: `url(${icon})`,
                          maskSize: 'contain',
                          maskRepeat: 'no-repeat',
                          maskPosition: 'center',
                        }}
                      />
                      <span className={`${active ? 'font-semibold' : 'font-medium'}`}>{t(titleKey)}</span>
                    </span>
                  </Link>
                </MenuItem>
              );
            })}

            {/* Dynamic Submenus */}
            {filteredSubmenus.map((submenu) => {
              // Check if any child item is active
              const hasActiveChild = submenu.items.some((item) => isActiveSubMenu(item.path));
              const isSubmenuActive = expandedMenu === submenu.key || hasActiveChild;
              
              return (
                <div key={submenu.key} className="flex flex-col w-full">
                  {/* Parent Menu */}
                  <MenuItem
                    onClick={() => handleToggle(submenu.key)}
                    className={`px-4 py-3 ${isSubmenuActive
                      ? 'bg-primary/15 text-primary'
                      : 'text-[#16283E]'
                      }`}
                    style={{
                      borderRadius: '8px',
                      margin: '0 10px',
                    }}
                  >
                    <div className="flex items-center justify-between w-full">
                      {/* Title and Icon */}
                      <div className="flex items-center gap-3">
                        <div
                          style={{
                            width: '20px',
                            height: '20px',
                            backgroundColor: isSubmenuActive ? primaryColor : '#000',
                            WebkitMaskImage: `url(${submenu.icon})`,
                            WebkitMaskSize: 'contain',
                            WebkitMaskRepeat: 'no-repeat',
                            WebkitMaskPosition: 'center',
                            maskImage: `url(${submenu.icon})`,
                            maskSize: 'contain',
                            maskRepeat: 'no-repeat',
                            maskPosition: 'center',
                          }}
                        />
                        <span className={`${isSubmenuActive ? 'font-semibold' : 'font-medium'}`}>{submenu.title}</span>
                      </div>
                      {/* Chevron */}
                      <div className="ml-auto">
                        <img
                          src={expandedMenu === submenu.key ? chevronUp : chevronDown}
                          alt="Chevron"
                          className="w-5 h-5"
                        />
                      </div>
                    </div>
                  </MenuItem>

                  {/* Submenu Items */}
                  {expandedMenu === submenu.key && (
                    <div className="pl-8 pt-2">
                      {submenu.items.map((item) => (
                        <MenuItem
                          key={item.path}
                          active={isActiveSubMenu(item.path)}
                          className={`relative px-4 py-2 ${isActiveSubMenu(item.path)
                            ? 'text-primary bg-transparent before:absolute before:left-0 before:top-0 before:bottom-0 before:w-[4px] before:bg-primary'
                            : 'text-[#16283E] before:absolute before:left-0 before:top-0 before:bottom-0 before:w-[1px] before:bg-gray-300'
                            }`}
                        >
                          <Link to={item.path} className={`${isActiveSubMenu(item.path) ? 'font-semibold' : 'font-medium'} w-full h-full flex items-center hover:text-primary transition-colors`}
                          >{item.title}</Link>
                        </MenuItem>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </Menu>

    </ProSidebar>
  );
};

export default Sidebar;
